import { Button, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleControllerGraphs } from 'stores/sensor/controllersSlice';
import { ControllerChartFields } from 'types/ControllerFields.js';
import RefreshButton from 'components/RefreshButton';

const GraphsFilterBar = ({ hasRadar = false, compact = false, showRefresh = true, onConfirm }) => {
  const selectedGraphs = useSelector(state => state.controllers.selectedGraphs);
  const controllerModel = useSelector(state => state.controllers.currentMeasurements?.controller_model);
  const isRelayModel = ['RELAY_2CH', 'RELAY_2CH_NC', 'RELAY_4CH', 'RELAY_4CH_NC'].includes(controllerModel);
  const hiddenGraphFields = useMemo(() => {
    const modelHiddenFields = isRelayModel
      ? ['on_off', 'fan_speed', 'temp_set_point', 'cool_water_valve']
      : ['channel_1', 'channel_2'];

    return hasRadar ? modelHiddenFields : [...modelHiddenFields, 'motion_counter'];
  }, [hasRadar, isRelayModel]);
  const visibleChartFields = useMemo(
    () => Object.entries(ControllerChartFields).filter(([, params]) => !hiddenGraphFields.includes(params.db_col_name)),
    [hiddenGraphFields],
  );
  const sanitizedSelectedGraphs = useMemo(() => {
    const replacedGraphs = selectedGraphs.flatMap(graph => {
      if (isRelayModel && graph === 'on_off') {
        return ['channel_1', 'channel_2'];
      }
      if (!isRelayModel && ['channel_1', 'channel_2'].includes(graph)) {
        return ['on_off'];
      }
      return [graph];
    });

    return [...new Set(replacedGraphs)].filter(graph => !hiddenGraphFields.includes(graph));
  }, [selectedGraphs, hiddenGraphFields, isRelayModel]);

  const [localSelectedGraphsArray, setLocalSelectedGraphsArray] = useState([...sanitizedSelectedGraphs]);
  const [isRefreshGlowing, setIsRefreshGlowing] = useState(false);
  // const selectedGraphs = useSelector(state => state.sensors.selectedGraphs);
  // console.log('selectedGraphs:', selectedGraphs);

  const dispatch = useDispatch();
  const checkboxSx = compact ? { p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1rem' } } : {};
  const compactGridColumns = Math.ceil(visibleChartFields.length / 3);
  const optionSx = {
    width: compact ? '100%' : 'auto',
    minWidth: compact ? 0 : undefined,
    flexShrink: 0,
    height: compact ? '4.8rem' : 'auto',
    m: compact ? 0 : undefined,
    marginRight: compact ? 0 : '2.5rem',
    flexDirection: 'column-reverse',
    alignItems: 'center',
    justifyContent: compact ? 'flex-start' : 'center',
    gap: compact ? 0.15 : undefined,
    overflow: 'visible',
    '& .MuiCheckbox-root': {
      flexShrink: 0,
    },
    '& .MuiFormControlLabel-label': {
      width: compact ? '100%' : undefined,
      minWidth: compact ? 0 : undefined,
      fontSize: compact ? '.76rem' : '1.1rem',
      textAlign: 'center',
      whiteSpace: compact ? 'normal' : 'nowrap',
    },
    '& .MuiSvgIcon-root': compact
      ? {
          width: '1.54rem',
          height: '1.54rem',
          fontSize: '1.54rem',
          flexShrink: 0,
          transform: 'none',
          scale: 1,
        }
      : undefined,
    '& .MuiSvgIcon-root > svg': compact
      ? {
          width: '100%',
          height: '100%',
        }
      : undefined,
  };

  useEffect(() => {
    setLocalSelectedGraphsArray(sanitizedSelectedGraphs);
  }, [sanitizedSelectedGraphs]);

  useEffect(() => {
    const selectedGraphsChanged =
      sanitizedSelectedGraphs.length !== selectedGraphs.length ||
      sanitizedSelectedGraphs.some((graph, index) => graph !== selectedGraphs[index]);

    if (selectedGraphsChanged) {
      dispatch(toggleControllerGraphs(sanitizedSelectedGraphs));
    }
  }, [dispatch, sanitizedSelectedGraphs, selectedGraphs]);

  const handleToggle = graphName => {
    if (localSelectedGraphsArray.length === 1 && localSelectedGraphsArray[0] === graphName) {
      alert('Please select at least 1 graph');
      return;
    }

    //     // localSelectedGraphsArray.includes(graphName) ? localSelectedGraphsArray.filter(name => name === graphName) : localSelectedGraphsArray.push(graphName);

    //     // dispatch(toggleGraphs(selectedGraphs));

    setLocalSelectedGraphsArray(
      prevSelected =>
        prevSelected.includes(graphName)
          ? prevSelected.filter(i => i !== graphName) // Remove if already selected
          : [...prevSelected, graphName], // Add if not selected
    );
    setIsRefreshGlowing(true);
  };

  const handleRefresh = () => {
    setIsRefreshGlowing(false);
    // console.log(localSelectedGraphsArray);
    if (localSelectedGraphsArray.length === 0) {
      alert('Please select at least 1 graph');
      return false;
    }
    dispatch(toggleControllerGraphs(localSelectedGraphsArray));
    return true;
  };

  const handleConfirm = () => {
    if (handleRefresh()) {
      onConfirm?.();
    }
  };

  return (
    <FormGroup
      sx={{
        ...(compact ? { height: '100%' } : {}),
        width: '100%',
        overflowX: compact ? 'hidden' : 'auto',
        overflowY: 'hidden',
        pt: compact ? 0.5 : 1.5,
        pb: compact ? 0.5 : 1,
        transform: compact ? 'none' : 'rotateX(180deg)',
        scrollbarWidth: 'thin',
        scrollbarColor: '#3244E2 #E7E9F8',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#E7E9F8',
          borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#3244E2',
          borderRadius: '999px',
          border: '2px solid #E7E9F8',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#2534B8',
        },
      }}>
      <Box
        sx={{
          display: compact ? 'grid' : 'flex',
          gridTemplateColumns: compact ? `repeat(${compactGridColumns}, minmax(0, 1fr))` : undefined,
          gridTemplateRows: compact ? 'repeat(3, max-content)' : undefined,
          transform: compact ? 'none' : 'rotateX(180deg)',
          alignItems: 'center',
          alignContent: compact ? 'center' : undefined,
          justifyContent: 'center',
          flexWrap: 'nowrap',
          // columnGap: compact ? 0.4 : undefined,
          columnGap: compact ? 0.25 : undefined,
          rowGap: compact ? 0 : undefined,
          gap: compact ? undefined : 0,
          minWidth: '100%',
          width: compact ? '100%' : 'max-content',
          marginTop: compact ? 0 : '.5rem',
          height: compact ? 'calc(100% - 44px)' : 'auto',
          overflow: 'visible',
        }}>
        {showRefresh && (
          <Box
            sx={{
              marginRight: compact ? '.35rem' : '1rem',
              p: 1.25,
              position: compact ? 'static' : 'sticky',
              left: 0,
              zIndex: 1,
            }}>
            <RefreshButton isGlowing={isRefreshGlowing} onClick={handleRefresh} />
          </Box>
        )}
        {visibleChartFields.map(([, params]) => {
          return (
            <FormControlLabel
              key={params.db_col_name}
              control={
                <Checkbox
                  sx={checkboxSx}
                  checked={localSelectedGraphsArray.includes(params.db_col_name)}
                  onChange={() => handleToggle(params.db_col_name)}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: compact ? 'column' : 'row',
                    justifyContent: 'center',
                    minWidth: 0,
                    width: '100%',
                    fontSize: compact ? '.81rem' : params.iconSize,
                    overflow: 'visible',
                    whiteSpace: compact ? 'normal' : 'nowrap',
                    lineHeight: compact ? 1.1 : undefined,
                    gap: compact ? 0.15 : undefined,
                    '& .HomeIconClass': {
                      width: compact ? '100%' : undefined,
                      height: compact ? '100%' : undefined,
                    },
                  }}>
                  {params.icon}
                  <Typography
                    sx={{
                      fontSize: compact ? '.76rem' : '.8rem',
                      lineHeight: compact ? 1.1 : undefined,
                      marginLeft: compact ? 0 : '1rem',
                      textAlign: 'center',
                      whiteSpace: compact ? 'normal' : 'nowrap',
                      overflowWrap: 'anywhere',
                      maxWidth: '100%',
                    }}>
                    {params.display_value}
                  </Typography>
                </Box>
              }
              sx={optionSx}
            />
          );
        })}
      </Box>
      {onConfirm && (
        <Box sx={{ display: 'flex', justifyContent: 'center', height: '36px', alignItems: 'center' }}>
          <Button variant="contained" size="small" sx={{ minWidth: '7rem' }} onClick={handleConfirm}>
            Confirm
          </Button>
        </Box>
      )}
    </FormGroup>
  );
};

GraphsFilterBar.propTypes = {
  hasRadar: PropTypes.bool,
  compact: PropTypes.bool,
  showRefresh: PropTypes.bool,
  onConfirm: PropTypes.func,
};

export default GraphsFilterBar;
